import React, { createContext, useContext, useState, useEffect } from 'react';
import { ModuleReleaseManager } from '../utils/moduleReleaseSystem';
import { apiService } from '../services/api';
import { useReadingProgress } from './ReadingProgressContext';

const ModuleReleaseContext = createContext();

export const useModuleRelease = () => {
  const context = useContext(ModuleReleaseContext);
  if (!context) {
    throw new Error('useModuleRelease must be used within a ModuleReleaseProvider');
  }
  return context;
};

export const ModuleReleaseProvider = ({ children }) => {
  const { sessionId } = useReadingProgress();
  const [moduleCompletions, setModuleCompletions] = useState({});
  const [releaseManager, setReleaseManager] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadModuleCompletions();
  }, [sessionId]);

  useEffect(() => {
    if (moduleCompletions) {
      // Create release manager with current completion data
      const manager = new ModuleReleaseManager(moduleCompletions);
      setReleaseManager(manager);
      setLoading(false);
    }
  }, [moduleCompletions]);

  const loadModuleCompletions = async () => {
    try {
      setLoading(true);
      const response = await apiService.getModuleCompletions(sessionId);
      setModuleCompletions(response.completions || {});
    } catch (error) {
      console.error('Error loading module completions:', error);
      setModuleCompletions({});
    } finally {
      setLoading(false);
    }
  };

  const markModuleCompleted = async (moduleId, quizCompleted = false, allSectionsRead = false) => {
    console.log(`🚀 CONTEXT: markModuleCompleted called with:`, {
      moduleId,
      quizCompleted,
      allSectionsRead,
      sessionId
    });
    
    try {
      // Optimistically update local state
      const completionData = {
        quizCompleted,
        allSectionsRead,
        completedAt: new Date().toISOString()
      };
      
      console.log(`📝 CONTEXT: Updating local state for ${moduleId}:`, completionData);
      setModuleCompletions(prev => ({
        ...prev,
        [moduleId]: completionData
      }));

      // Update backend
      console.log(`📡 CONTEXT: Calling backend API updateModuleCompletion...`);
      await apiService.updateModuleCompletion(sessionId, moduleId, quizCompleted, allSectionsRead);
      console.log(`✅ CONTEXT: Backend API call successful`);
      
      // Refresh completions to get latest state
      console.log(`🔄 CONTEXT: Refreshing completion data from backend...`);
      await loadModuleCompletions();
      console.log(`✅ CONTEXT: Module completion process finished for ${moduleId}`);
      
    } catch (error) {
      console.error('❌ CONTEXT: Error marking module as completed:', error);
      // Revert optimistic update on error
      loadModuleCompletions();
    }
  };

  const isModuleAccessible = (moduleId) => {
    if (!releaseManager) return false;
    const status = releaseManager.getModuleStatus(moduleId);
    return status === 'available' || status === 'completed';
  };

  const getModuleStatus = (moduleId) => {
    if (!releaseManager) return 'coming_soon';
    return releaseManager.getModuleStatus(moduleId);
  };

  const getAllModulesWithStatus = () => {
    if (!releaseManager) return [];
    return releaseManager.getAllModulesWithStatus();
  };

  const getProgressSummary = () => {
    if (!releaseManager) return { available: 0, completed: 0, total: 6, percentageComplete: 0, currentWeek: 1 };
    return releaseManager.getProgressSummary();
  };

  const getDaysUntilNextRelease = () => {
    if (!releaseManager) return null;
    return releaseManager.getDaysUntilNextRelease();
  };

  const getNextModuleToUnlock = () => {
    if (!releaseManager) return null;
    return releaseManager.getNextModuleToUnlock();
  };

  const value = {
    moduleCompletions,
    loading,
    markModuleCompleted,
    isModuleAccessible,
    getModuleStatus,
    getAllModulesWithStatus,
    getProgressSummary,
    getDaysUntilNextRelease,
    getNextModuleToUnlock,
    loadModuleCompletions
  };

  return (
    <ModuleReleaseContext.Provider value={value}>
      {children}
    </ModuleReleaseContext.Provider>
  );
};